import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getServices, getHomePage, getTestimonials } from '../utils/api';
import { decodeHTMLEntities } from '../utils/helpers';



export default function HomePage() {
    const [dynamicServices, setDynamicServices] = useState([]);
    const [homeData, setHomeData] = useState(null);
    const [loading, setLoading] = useState(true);

    const safeJsonParse = (str, fallback) => {
        if (!str || typeof str !== 'string') return fallback;
        try {
            return JSON.parse(str);
        } catch (e) {
            console.error("JSON Parse Error:", e);
            return fallback;
        }
    };

    useEffect(() => {
        const loadAllData = async () => {
            const [servicesData, pageData, testimonialsData] = await Promise.all([
                getServices(),
                getHomePage(),
                getTestimonials()
            ]);

            if (servicesData) {
                const mapped = servicesData.map(item => {
                    const sm = item.service_meta || item.meta || {};
                    return {
                        id: item.id,
                        icon: sm.icon || sm.service_icon || '🏗️',
                        title: decodeHTMLEntities(item.title.rendered),
                        desc: decodeHTMLEntities(item.excerpt.rendered.replace(/<[^>]+>/g, '') || item.content.rendered.replace(/<[^>]+>/g, '').substring(0, 150) + '...'),
                        tags: sm.tags ? sm.tags.split(',') : (sm.service_tags ? sm.service_tags.split(',') : []),
                        size: sm.size || sm.service_size || 'small',
                        slug: item.slug
                    };
                });
                const sorted = [...mapped].sort((a, b) => (b.size === 'large' ? 1 : -1));
                setDynamicServices(sorted);
            }

            if (pageData) {
                const meta = pageData.home_meta || {};
                setHomeData({
                    hero_title: decodeHTMLEntities(meta.hero_title || "We Build Digital Experiences That Grow Your Business."),
                    hero_desc: decodeHTMLEntities(meta.hero_desc || "Elevate your digital presence with a boutique agency delivering cutting-edge web architectures and data-driven, compounding growth."),
                    stats: safeJsonParse(meta.stats, [
                        { value: '12+', label: 'Years Building' },
                        { value: '250+', label: 'Projects Delivered' },
                        { value: '98%', label: 'Client Retention' }
                    ]),
                    process: safeJsonParse(meta.process, [
                        { num: '01', title: 'Discovery', desc: 'Deep-dive workshops to understand your goals, audience, and competitive landscape.' },
                        { num: '02', title: 'Planning', desc: 'Architecture blueprints, wireframes, and a roadmap aligned with business objectives.' },
                        { num: '03', title: 'Development', desc: 'Agile sprints with weekly deliveries and transparent progress tracking.' },
                        { num: '04', title: 'Launch', desc: 'Rigorous QA, performance audits, and a staged deployment to production.' },
                        { num: '05', title: 'Growth', desc: 'Ongoing support, analytics monitoring, and iterative improvements post-launch.' }
                    ]),
                    testimonials: testimonialsData ? testimonialsData.map(t => {
                        const tm = t.service_meta || t.meta || {};
                        return {
                            quote: t.content.rendered.replace(/<[^>]+>/g, ''),
                            author: t.title.rendered,
                            role: tm.role || tm.testimonial_role || '',
                            company: tm.company || tm.testimonial_company || ''
                        };
                    }) : safeJsonParse(meta.testimonials, [
                        { quote: '"The digital capabilities brought to our brand are unparalleled. They don\'t just build sites — they build momentum."', author: 'Maria Stelios', role: 'CEO, Prism Digital' },
                        { quote: '"Revenue grew 230% while our workload of managing our site fell 80%. Their elite team appeared to be a game changer for us."', author: 'Ethan Rollins', role: 'Founder, NovaMind' }
                    ]),
                    portfolio: safeJsonParse(meta.portfolio, [
                        { tag: 'FINTECH', title: 'EtherFinance', category: 'React Dashboard Webapp', color: 'from-blue-900/60 to-indigo-900/40' },
                        { tag: 'ARCHITECTURE', title: 'Aurelius Studio', category: '2 nominations 2024', color: 'from-amber-900/40 to-orange-900/30' }
                    ]),
                    about_teaser_title: decodeHTMLEntities(meta.about_teaser_title || "Guesting the future of online presence."),
                    about_teaser_desc: decodeHTMLEntities(meta.about_teaser_desc || "Digital Architects at Work"),
                    about_mission: decodeHTMLEntities(meta.about_mission || "To engineer radically honest digital architectures that consistently outperform and age gracefully."),
                    about_focus: decodeHTMLEntities(meta.about_focus || "From our client network of enterprise brands to emerging startups — quality without compromise."),
                    cta_title: decodeHTMLEntities(meta.cta_title || "Start Your Evolution"),
                    cta_desc: decodeHTMLEntities(meta.cta_desc || "Partner with the curators of the new digital age and dominate your industry's scene.")
                });
            } else {
                // Fallback structure
                setHomeData({
                    hero_title: "We Build Digital Experiences That Grow Your Business.",
                    hero_desc: "Elevate your digital presence with a boutique agency delivering cutting-edge web architectures and data-driven, compounding growth.",
                    stats: [
                        { value: '12+', label: 'Years Building' },
                        { value: '250+', label: 'Projects Delivered' },
                        { value: '98%', label: 'Client Retention' }
                    ],
                    process: [
                        { num: '01', title: 'Discovery', desc: 'Deep-dive workshops to understand your goals, audience, and competitive landscape.' },
                        { num: '02', title: 'Planning', desc: 'Architecture blueprints, wireframes, and a roadmap aligned with business objectives.' },
                        { num: '03', title: 'Development', desc: 'Agile sprints with weekly deliveries and transparent progress tracking.' },
                        { num: '04', title: 'Launch', desc: 'Rigorous QA, performance audits, and a staged deployment to production.' },
                        { num: '05', title: 'Growth', desc: 'Ongoing support, analytics monitoring, and iterative improvements post-launch.' }
                    ],
                    testimonials: testimonialsData ? testimonialsData.map(t => {
                        const tm = t.service_meta || t.meta || {};
                        return {
                            quote: decodeHTMLEntities(t.content.rendered.replace(/<[^>]+>/g, '')),
                            author: decodeHTMLEntities(t.title.rendered),
                            role: decodeHTMLEntities(tm.role || tm.testimonial_role || ''),
                            company: decodeHTMLEntities(tm.company || tm.testimonial_company || '')
                        };
                    }) : [
                        { quote: '"The digital capabilities brought to our brand are unparalleled. They don\'t just build sites — they build momentum."', author: 'Maria Stelios', role: 'CEO, Prism Digital' },
                        { quote: '"Revenue grew 230% while our workload of managing our site fell 80%. Their elite team appeared to be a game changer for us."', author: 'Ethan Rollins', role: 'Founder, NovaMind' }
                    ],
                    portfolio: [
                        { tag: 'FINTECH', title: 'EtherFinance', category: 'React Dashboard Webapp', color: 'from-blue-900/60 to-indigo-900/40' },
                        { tag: 'ARCHITECTURE', title: 'Aurelius Studio', category: '2 nominations 2024', color: 'from-amber-900/40 to-orange-900/30' }
                    ],
                    about_teaser_title: "Guesting the future of online presence.",
                    about_teaser_desc: "Digital Architects at Work",
                    about_mission: "To engineer radically honest digital architectures that consistently outperform and age gracefully.",
                    about_focus: "From our client network of enterprise brands to emerging startups — quality without compromise.",
                    cta_title: "Start Your Evolution",
                    cta_desc: "Partner with the curators of the new digital age and dominate your industry's scene."
                });
            }
            setLoading(false);
        };
        loadAllData();
    }, []);

    // Fallback to static if no data yet (or show loading)
    const displayServices = dynamicServices.length > 0 ? dynamicServices : [];
    return (
        <div>
            {/* Hero */}
            <section className="relative min-h-screen flex items-center bg-grid overflow-hidden">
                <div className="absolute inset-0 bg-hero-glow pointer-events-none" />
                <div className="max-w-7xl mx-auto px-6 sm:px-8 pt-32 pb-20 grid md:grid-cols-2 gap-12 items-center">
                    <div>
                        <p className="section-tag">✦ Digital Pulse for Entrepreneurs</p>
                        <h1 className="text-5xl md:text-6xl font-black leading-tight mb-6 text-nocturne-text uppercase">
                            {homeData?.hero_title.split(' ').slice(0, 2).join(' ')} <span className="gradient-text">{homeData?.hero_title.split(' ').slice(2, 3)}</span><br />
                            {homeData?.hero_title.split(' ').slice(3, 5).join(' ')}<br />
                            {homeData?.hero_title.split(' ').slice(5, 7).join(' ')}<br />
                            <span className="gradient-text">{homeData?.hero_title.split(' ').slice(7).join(' ')}</span>
                        </h1>
                        <p className="text-nocturne-muted text-lg leading-relaxed mb-8 max-w-md">
                            {homeData?.hero_desc}
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <Link to="/contact" className="btn-primary px-8 py-3.5">
                                Get Started
                            </Link>
                            <Link to="/portfolio" className="btn-outline px-8 py-3.5">
                                View Portfolio →
                            </Link>
                        </div>
                    </div>

                    {/* Hero card mockup */}
                    <div className="hidden md:flex justify-end">
                        <div className="relative">
                            <div className="w-[420px] h-[300px] card glow-purple p-0 overflow-hidden rounded-2xl">
                                <div className="flex items-center gap-2 px-4 py-3 border-b border-nocturne-border">
                                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
                                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
                                    <span className="text-nocturne-muted text-xs ml-2 font-mono">amisubsolutions_dashboard</span>
                                </div>
                                <div className="p-4 font-mono text-xs space-y-2">
                                    <div className="text-nocturne-purple">→ Initializing project...</div>
                                    <div className="text-green-400">✓ Architecture designed</div>
                                    <div className="text-green-400">✓ Components built</div>
                                    <div className="text-green-400">✓ Performance: 99/100</div>
                                    <div className="text-nocturne-cyan">▸ Deploying to edge...</div>
                                    <div className="flex gap-2 mt-4">
                                        <div className="flex-1 h-8 bg-nocturne-purple/20 rounded border border-nocturne-purple/30 flex items-center justify-center text-nocturne-purple-light text-xs">Button</div>
                                        <div className="flex-1 h-8 bg-nocturne-border rounded flex items-center justify-center text-nocturne-muted text-xs">Outline</div>
                                    </div>
                                    <div className="h-1 bg-nocturne-border rounded-full mt-2">
                                        <div className="h-1 bg-nocturne-purple rounded-full w-4/5 animate-pulse-slow" />
                                    </div>
                                </div>
                            </div>
                            {/* Floating mini-cards */}
                            <div className="absolute -bottom-8 -left-10 card p-3 text-xs glow-sm">
                                <div className="text-nocturne-muted mb-1">Performance</div>
                                <div className="text-2xl font-bold text-nocturne-text">99<span className="text-nocturne-purple">/100</span></div>
                            </div>
                            <div className="absolute -top-6 -right-4 card p-3 text-xs glow-sm">
                                <div className="text-nocturne-muted mb-1">Uptime</div>
                                <div className="text-2xl font-bold gradient-text">99.9%</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Service List section */}
            <section className="py-24 px-6 sm:px-8  ">
                <div className="max-w-7xl mx-auto">
                    {loading ? (
                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="card h-64 animate-pulse bg-nocturne-card/50" />
                            <div className="card h-64 animate-pulse bg-nocturne-card/50" />
                        </div>
                    ) : (
                        <>
                            <div className="grid md:grid-cols-2 gap-6 mb-6">
                                {displayServices.slice(0, 2).map((s, i) => (
                                    <Link key={s.id} to={`/service/${s.slug}`} className="card group flex flex-col cursor-pointer">
                                        <div className={`w-12 h-12 rounded-xl border flex items-center justify-center mb-6 transition-colors overflow-hidden ${i === 0 ? 'bg-nocturne-purple/10 border-nocturne-purple/30 text-nocturne-purple font-mono text-lg group-hover:bg-nocturne-purple/20' : 'bg-purple-900/30 border-purple-700/40 text-2xl'}`}>
                                            {s.icon}
                                        </div>
                                        <h2 className="text-2xl font-bold text-nocturne-text mb-3">{s.title}</h2>
                                        <p className="text-nocturne-muted text-sm leading-relaxed mb-6">{s.desc}</p>

                                        {s.tags && s.tags.length > 0 && (
                                            <div className="grid grid-cols-2 gap-2 mt-auto">
                                                {s.tags.map((tag, ti) => (
                                                    <div key={ti} className="flex items-center gap-2 text-nocturne-muted text-xs">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-nocturne-purple" />
                                                        {tag}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Phone mockup for mobile apps (index 1 usually) */}
                                        {s.title.toLowerCase().includes('mobile') && (
                                            <div className="mt-auto flex gap-3 justify-center pt-6">
                                                {[...Array(2)].map((_, mi) => (
                                                    <div key={mi} className="w-20 h-36 bg-nocturne-bg rounded-2xl border border-nocturne-border flex flex-col overflow-hidden">
                                                        <div className="h-2 bg-nocturne-border mx-4 mt-2 rounded-full" />
                                                        <div className="flex-1 p-2 space-y-1.5">
                                                            <div className="h-1.5 bg-nocturne-border rounded" />
                                                            <div className="h-1.5 bg-nocturne-purple/30 rounded w-3/4" />
                                                            <div className="h-1.5 bg-nocturne-border rounded w-1/2" />
                                                            <div className="mt-3 h-8 bg-nocturne-purple/20 rounded-lg" />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {!s.title.toLowerCase().includes('mobile') && (
                                            <span className="mt-6 text-left text-nocturne-muted group-hover:text-nocturne-purple text-sm transition-colors">
                                                → View details
                                            </span>
                                        )}
                                    </Link>
                                ))}
                            </div>

                            {/* Bottom row: remaining services */}
                            <div className="grid md:grid-cols-3 gap-6">
                                {displayServices.slice(2).map((s) => (
                                    <Link key={s.id} to={`/service/${s.slug}`} className="card group cursor-pointer">
                                        <div className="w-10 h-10 rounded-lg bg-nocturne-bg border border-nocturne-border flex items-center justify-center text-xl mb-4 group-hover:border-nocturne-purple/40 transition-colors overflow-hidden">
                                            {s.icon}
                                        </div>
                                        <h3 className="text-nocturne-text font-bold text-lg mb-3">{s.title}</h3>
                                        <p className="text-nocturne-muted text-sm leading-relaxed">{s.desc}</p>
                                        <span className="mt-4 block text-nocturne-muted group-hover:text-nocturne-purple text-sm transition-colors">→</span >
                                    </Link>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </section>

            {/* About teaser / stats */}
            <section className="py-24 px-6 sm:px-8 bg-nocturne-card border-y border-nocturne-border">
                <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
                    {/* Left – image placeholder */}
                    <div className="relative">
                        <div className="rounded-2xl overflow-hidden aspect-[4/3] bg-gradient-to-br from-nocturne-purple/20 to-nocturne-cyan/10 border border-nocturne-border flex items-center justify-center">
                            <div className="text-center p-8">
                                <div className="text-6xl mb-4">🧠</div>
                                <p className="text-nocturne-muted text-sm">{homeData?.about_teaser_desc}</p>
                            </div>
                        </div>
                        {/* Stats overlay card */}
                        <div className="absolute bottom-4 left-4 right-4 card flex items-center justify-around py-5">
                            {homeData?.stats.map((s, i) => (
                                <div key={i} className="text-center">
                                    <div className="text-3xl font-black gradient-text">{s.value}</div>
                                    <div className="text-nocturne-muted text-xs mt-1">{s.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right – copy */}
                    <div>
                        <p className="section-tag">We are digital architects</p>
                        <h2 className="text-4xl font-bold text-nocturne-text mb-6 leading-snug">
                            {homeData?.about_teaser_title.split(' ').slice(0, -3).join(' ')} <span className="gradient-text">{homeData?.about_teaser_title.split(' ').slice(-3).join(' ')}</span>
                        </h2>
                        <div className="space-y-4 mb-8">
                            {[
                                { label: 'Mission', desc: homeData?.about_mission },
                                { label: 'Focus', desc: homeData?.about_focus },
                            ].map((item, i) => (
                                <div key={i} className="flex gap-3">
                                    <div className="w-1.5 rounded-full bg-nocturne-purple shrink-0 mt-1" />
                                    <div>
                                        <div className="text-nocturne-text font-semibold text-sm">{item.label}</div>
                                        <div className="text-nocturne-muted text-sm">{item.desc}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Link to="/about" className="btn-primary inline-block">
                            Learn About Us →
                        </Link>
                    </div>
                </div>
            </section>

            {/* Process */}
            <section className="py-24 px-6 sm:px-8 bg-nocturne-card border-y border-nocturne-border">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-nocturne-text">The <span className="gradient-text">Amisubsolutions</span> Method</h2>
                        <p className="text-nocturne-muted mt-4 max-w-md mx-auto">A systematic approach to the design and delivery of high-quality digital experiences.</p>
                    </div>
                    <div className="flex flex-col md:flex-row gap-0 relative">
                        <div className="hidden md:block absolute top-10 left-0 right-0 h-px bg-nocturne-border z-0" />
                        {homeData?.process.map((step, i) => (
                            <div key={i} className="flex-1 relative z-10 px-4 text-center">
                                <div className="w-10 h-10 rounded-full bg-nocturne-card border-2 border-nocturne-purple flex items-center justify-center text-nocturne-purple font-bold text-sm mx-auto mb-4">
                                    {step.num}
                                </div>
                                <h3 className="text-nocturne-text font-semibold mb-2">{step.title}</h3>
                                <p className="text-nocturne-muted text-xs leading-relaxed">{step.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-24 px-6 pb-6 sm:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <p className="section-tag">Partners in Success</p>
                        <h2 className="text-3xl font-bold text-nocturne-text">Trusted by the <span className="gradient-text">builders</span> of the new digital era.</h2>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                        {homeData?.testimonials.map((t, i) => (
                            <div key={i} className="card relative">
                                <div className="text-nocturne-purple text-4xl font-serif mb-4">"</div>
                                <p className="text-nocturne-text text-sm leading-relaxed mb-6">{t.quote}</p>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-nocturne-purple/20 border border-nocturne-purple/40 flex items-center justify-center text-nocturne-purple font-bold text-sm">
                                        {t.author ? t.author[0] : 'U'}
                                    </div>
                                    <div>
                                        <div className="text-nocturne-text font-semibold text-sm">{t.author}</div>
                                        <div className="text-nocturne-muted text-xs">{t.role}{t.company ? ` @ ${t.company}` : ''}</div>
                                    </div>
                                    <div className="ml-auto flex gap-0.5">
                                        {[...Array(5)].map((_, si) => <span key={si} className="text-yellow-400 text-sm">★</span>)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA / Contact teaser */}
            <section className="py-24 px-6 sm:px-8">
                <div className="max-w-4xl mx-auto">
                    <div className="card border-nocturne-purple/30 bg-card-gradient text-center py-16 px-8 glow-purple">
                        <h2 className="text-4xl md:text-5xl font-black text-nocturne-text mb-6 uppercase tracking-tight">
                            {homeData?.cta_title.split(' ').slice(0, -1).join(' ')} <span className="gradient-text">{homeData?.cta_title.split(' ').pop()}</span>
                        </h2>
                        <p className="text-nocturne-muted mb-10 max-w-md mx-auto">
                            {homeData?.cta_desc}
                        </p>
                        <Link to="/contact" className="btn-primary inline-block px-10 py-4 text-base">
                            Synthesize →
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
